<template>
  <div>
    <Welcome v-if="stepShown === 0 && !scopes" @next-step="nextStep" />
    <Privacy v-if="stepShown === 1 && !scopes" @next-step="nextStep" />
    <Auth
      v-if="stepShown === 2 && !scopes"
      :google-flow-started="googleFlowStarted"
      :email-flow-started="emailFlowStarted"
      :show-back="stepShown > 0 && !isSignin"
      :mode="isSignup ? 'signup' : 'signin'"
      @start-google-flow="startGoogleFlow"
      @start-email-flow="startEmailFlow"
      @prev-step="prevStep"
    />
    <!-- GMAIL INTEGRATION -->
    <GmailLogin
      v-else-if="is_grant_gmail"
      :button-text="buttonText"
      :google-flow-started="googleFlowStarted"
      @start-google-flow="startGoogleFlow"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref, toRefs, watch } from "vue";
import { useRoute } from "vue-router";

import { GoogleScope } from "@ctrl/controller-rpc";

import Auth from "@/components/login/Auth.vue";
import GmailLogin from "@/components/login/GmailLogin.vue";
import Privacy from "@/components/login/Privacy.vue";
import Welcome from "@/components/login/Welcome.vue";
import { metrics } from "@/metrics";
import { RouteNames } from "@/routes/types";
import { openExternalUrl } from "@/utils/open-external-url";

export default defineComponent({
  components: {
    Auth,
    GmailLogin,
    Privacy,
    Welcome,
  },
  props: {
    deeplink: Boolean,
    scopes: { type: Object as PropType<GoogleScope>, required: false },
    uri: { type: String, required: true },
  },
  emits: ["back"],
  setup(props, { emit }) {
    const { deeplink, scopes, uri } = toRefs(props);
    const googleFlowStarted = ref(false);
    const emailFlowStarted = ref(false);

    const route = useRoute();
    const isSignup = route.name === RouteNames.Signup;
    const isSignin = route.name === RouteNames.Signin;
    const stepShown = ref(isSignin ? 2 : 0);
    const nextStep = () => stepShown.value++;
    const prevStep = () => stepShown.value--;

    const is_grant_gmail = computed(() => scopes?.value && scopes.value.gmail);

    const buttonText = computed(() => {
      /* FIXME - Uncomment when google has validate gmail integration
            if (googleFlowStarted.value) {
        return "Check your default browser to continue";
      }

      if (is_grant_gmail.value) {
        return "Continue on Google";
      }*/

      return "Sign in with Google";
    });

    const openUrl = (url: string) => openExternalUrl(url, null, false);

    const startGoogleFlow = async () => {
      googleFlowStarted.value = true;
      let rewardful_referral = metrics.rewardful.getReferralId();
      var actual_uri = new URL(uri.value);
      if (rewardful_referral) {
        actual_uri.searchParams.append(`referral`, rewardful_referral);
      }
      if (deeplink.value) openExternalUrl(actual_uri.toString(), null);
      else window.location.assign(actual_uri.toString());
    };

    const startEmailFlow = async (email: string) => {
      emailFlowStarted.value = true;
      let actual_uri = new URL(uri.value);
      actual_uri.searchParams.append("provider", "email");
      actual_uri.searchParams.append("email", email);
      let rewardful_referral = metrics.rewardful.getReferralId();
      if (rewardful_referral) {
        actual_uri.searchParams.append("referral", rewardful_referral);
      }
      if (deeplink.value) openExternalUrl(actual_uri.toString(), null);
      else window.location.assign(actual_uri.toString());
    };

    function back() {
      emit("back");
    }

    watch(googleFlowStarted, () =>
      setTimeout(() => (googleFlowStarted.value = false), 5000)
    );
    watch(emailFlowStarted, () =>
      setTimeout(() => (emailFlowStarted.value = false), 5000)
    );
    return {
      back,
      buttonText,
      isSignin,
      isSignup,
      emailFlowStarted,
      googleFlowStarted,
      is_grant_gmail,
      nextStep,
      openUrl,
      prevStep,
      startEmailFlow,
      startGoogleFlow,
      stepShown,
    };
  },
});
</script>
